import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LanguageProvider, useLanguage } from '../contexts/LanguageContext';
import QRCode from 'qrcode';

interface DemoStep {
  id: string;
  title: string;
  description: string;
  component: React.ReactNode;
  duration: number;
  autoAdvance?: boolean;
}

interface MockCoupon {
  id: number;
  title: string;
  description: string;
  discount: string;
  vendor_name: string;
  vendor_address: string;
  vendor_category: string;
  driver_name: string;
  max_uses: number;
  current_uses: number;
}

interface MockStats {
  total_earnings: number;
  total_redemptions: number;
  active_coupons: number;
  vendors_recruited: number;
  this_month_earnings: number;
  this_week_earnings: number;
  recent_redemptions: Array<{
    id: number;
    coupon_title: string;
    vendor_name: string;
    commission_earned: number;
    redeemed_at: string;
  }>;
}

const mockCoupons: MockCoupon[] = [
  {
    id: 1,
    title: "Pizza 30% OFF",
    description: "Válido para pizzas grandes e família",
    discount: "30% OFF",
    vendor_name: "Pizzaria do João",
    vendor_address: "Rua Augusta, 1234 - Vila Madalena",
    vendor_category: "restaurant",
    driver_name: "Carlos Silva",
    max_uses: 50,
    current_uses: 23
  },
  {
    id: 2,
    title: "Desconto Farmácia",
    description: "20% em medicamentos e produtos",
    discount: "20% OFF",
    vendor_name: "Farmácia Central",
    vendor_address: "Av. Paulista, 567 - Bela Vista",
    vendor_category: "pharmacy",
    driver_name: "Carlos Silva",
    max_uses: 100,
    current_uses: 45
  }
];

const mockStats: MockStats = {
  total_earnings: 1847.50,
  total_redemptions: 68,
  active_coupons: 8,
  vendors_recruited: 5,
  this_month_earnings: 520.75,
  this_week_earnings: 127.25,
  recent_redemptions: [
    {
      id: 1,
      coupon_title: "Pizza 30% OFF",
      vendor_name: "Pizzaria do João",
      commission_earned: 18.50,
      redeemed_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 2,
      coupon_title: "Café Grátis",
      vendor_name: "Café da Esquina",
      commission_earned: 12.00,
      redeemed_at: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 3,
      coupon_title: "Desconto Farmácia",
      vendor_name: "Farmácia Central",
      commission_earned: 8.25,
      redeemed_at: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString()
    }
  ]
};

const DemoFlowContent: React.FC = () => {
  const { t } = useLanguage();
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [generatedQR, setGeneratedQR] = useState<string>('');
  const [showQRModal, setShowQRModal] = useState(false);
  const [scannerActive, setScannerActive] = useState(false);
  const [validationResult, setValidationResult] = useState<any>(null);

  useEffect(() => {
    // Generate a mock QR code
    QRCode.toDataURL('DEMO_QR_TOKEN_12345', {
      width: 300,
      margin: 2,
      color: {
        dark: '#000000',
        light: '#ffffff',
      },
    }).then(setGeneratedQR);
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(dateString));
  };

  const demoSteps: DemoStep[] = [
    {
      id: 'welcome',
      title: t('demo.welcome.title') || 'Bem-vindo ao QRSP!',
      description: t('demo.welcome.desc') || 'Vamos fazer um tour completo pela plataforma de cupons QR',
      duration: 3000,
      component: (
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="h-full flex items-center justify-center bg-gradient-to-br from-green-600 to-green-700"
        >
          <div className="text-center text-white p-8">
            <motion.div
              initial={{ y: -20 }}
              animate={{ y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-6xl mb-6"
            >
              📱
            </motion.div>
            <motion.h1
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="text-3xl font-bold mb-4"
            >
              QRSP Demo
            </motion.h1>
            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.9 }}
              className="text-xl opacity-90"
            >
              São Paulo QR Coupon Platform
            </motion.p>
          </div>
        </motion.div>
      )
    },
    {
      id: 'generate',
      title: t('demo.generate.title') || 'Gerar QR Codes',
      description: t('demo.generate.desc') || 'Veja como gerar códigos QR para seus clientes',
      duration: 5000,
      component: (
        <div className="h-full flex flex-col bg-gray-50">
          <div className="bg-white p-4 shadow-sm">
            <h1 className="text-xl font-bold text-gray-900">
              {t('generate.title') || 'Gerar QR Codes'}
            </h1>
            <p className="text-gray-600 text-sm">
              {t('generate.subtitle') || 'Crie códigos para seus clientes'}
            </p>
          </div>

          <div className="bg-white p-4 border-b">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('generate.phone.label') || '📞 Telefone do Cliente (Opcional)'}
            </label>
            <motion.input
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.5 }}
              type="tel"
              value="+55 11 99999-8888"
              readOnly
              className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 text-lg"
            />
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-3">
              {mockCoupons.map((coupon, index) => (
                <motion.div
                  key={coupon.id}
                  initial={{ x: -50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 1 + index * 0.3 }}
                  className="bg-white rounded-xl p-4 shadow-sm border border-gray-200"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-xl">
                          {coupon.vendor_category === 'restaurant' ? '🍽️' : '💊'}
                        </span>
                        <h3 className="font-bold text-gray-900">{coupon.title}</h3>
                      </div>
                      <p className="text-sm text-gray-600 mb-1">{coupon.vendor_name}</p>
                      <p className="text-xs text-gray-500">{coupon.vendor_address}</p>
                    </div>
                    <div className="text-right">
                      <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-bold">
                        {coupon.discount}
                      </div>
                    </div>
                  </div>

                  <p className="text-sm text-gray-700 mb-3">{coupon.description}</p>

                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <span>🚗 {coupon.driver_name}</span>
                      <span className="text-green-600">
                        📊 {coupon.current_uses}/{coupon.max_uses} {t('generate.used') || 'usados'}
                      </span>
                    </div>
                  </div>

                  {index === 0 && (
                    <motion.button
                      initial={{ scale: 0.9, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: 2 }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setShowQRModal(true)}
                      className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-xl font-bold transition-all"
                    >
                      📱 {t('generate.button') || 'Gerar QR Code'}
                    </motion.button>
                  )}
                  {index === 1 && (
                    <button className="w-full bg-gray-200 text-gray-500 py-3 rounded-xl font-bold">
                      📱 {t('generate.button') || 'Gerar QR Code'}
                    </button>
                  )}
                </motion.div>
              ))}
            </div>
          </div>

          <AnimatePresence>
            {showQRModal && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
              >
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.8, opacity: 0 }}
                  className="bg-white rounded-2xl p-6 w-full max-w-sm"
                >
                  <div className="text-center">
                    <div className="mb-4">
                      <img
                        src={generatedQR}
                        alt="Demo QR Code"
                        className="mx-auto border-2 border-gray-200 rounded-lg"
                      />
                    </div>
                    
                    <h3 className="font-bold text-lg mb-1">{mockCoupons[0].title}</h3>
                    <p className="text-gray-600 text-sm mb-2">{mockCoupons[0].vendor_name}</p>
                    <div className="bg-green-100 text-green-800 px-4 py-2 rounded-full text-lg font-bold mb-4">
                      {mockCoupons[0].discount}
                    </div>
                    
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6">
                      <p className="text-xs text-yellow-800 font-medium">
                        ⏰ {t('generate.expires', { minutes: 5 }) || 'Expira em 5 minutos'}
                      </p>
                      <p className="text-xs text-yellow-700 mt-1">
                        {t('generate.show.desc') || 'Motre este código para o cliente escanear'}
                      </p>
                    </div>

                    <button
                      onClick={() => setShowQRModal(false)}
                      className="w-full bg-green-600 text-white py-3 rounded-xl font-bold"
                    >
                      ✅ {t('generate.success') || 'Código Gerado'}
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )
    },
    {
      id: 'scanner',
      title: t('demo.scanner.title') || 'Scanner QR',
      description: t('demo.scanner.desc') || 'Veja como validar cupons dos clientes',
      duration: 6000,
      component: (
        <div className="h-full flex flex-col bg-black">
          <div className="bg-black text-white p-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-bold">Scanner QR</h1>
                <p className="text-gray-300 text-sm">Validar cupons de desconto</p>
              </div>
              <div className="text-right">
                <div className="text-xs px-2 py-1 rounded bg-green-600">
                  {scannerActive ? 'ATIVO' : 'PARADO'}
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 relative">
            {!scannerActive ? (
              <div className="h-full flex items-center justify-center bg-gray-900">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center text-white p-8"
                >
                  <div className="text-6xl mb-4">📷</div>
                  <h2 className="text-xl font-semibold mb-2">Câmera Parada</h2>
                  <p className="text-gray-300 text-sm mb-6">
                    Toque no botão para iniciar o scanner
                  </p>
                </motion.div>
              </div>
            ) : (
              <div className="h-full bg-gray-800 relative">
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="absolute inset-0 bg-gradient-to-br from-gray-700 to-gray-900"
                >
                  <div className="absolute inset-0 flex items-center justify-center">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 1 }}
                      className="w-48 h-48 border-4 border-green-500 rounded-lg relative"
                    >
                      <motion.div
                        animate={{ y: [0, 180, 0] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                        className="absolute inset-x-0 top-0 h-1 bg-green-400 opacity-75"
                      />
                      <motion.div
                        initial={{ scale: 0.5, opacity: 0.3 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 2 }}
                        className="absolute inset-4 bg-white rounded flex items-center justify-center"
                      >
                        <div className="w-full h-full bg-black rounded opacity-20" />
                      </motion.div>
                    </motion.div>
                  </div>
                  
                  <AnimatePresence>
                    {scannerActive && (
                      <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 3 }}
                        className="absolute bottom-20 left-4 right-4"
                      >
                        <div className="bg-green-600 text-white p-4 rounded-lg text-center">
                          <div className="text-2xl mb-2">✅</div>
                          <h3 className="font-bold mb-1">Cupom Válido!</h3>
                          <p className="text-sm">Pizza 30% OFF - Pizzaria do João</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
                
                {scannerActive && (
                  <div className="absolute top-4 left-4 right-4">
                    <motion.div
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-black bg-opacity-70 text-white p-3 rounded-lg"
                    >
                      <p className="text-sm text-center">
                        📱 Aponte para o código QR do cliente
                      </p>
                    </motion.div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="bg-black p-4">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setScannerActive(!scannerActive)}
              className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${
                scannerActive
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
            >
              {scannerActive ? '⏹️ Parar Scanner' : '▶️ Iniciar Scanner'}
            </motion.button>
          </div>
        </div>
      )
    },
    {
      id: 'stats',
      title: t('demo.stats.title') || 'Seus Ganhos',
      description: t('demo.stats.desc') || 'Acompanhe suas comissões e estatísticas',
      duration: 5000,
      component: (
        <div className="h-full flex flex-col bg-gray-50">
          <div className="bg-white p-4 shadow-sm">
            <h1 className="text-xl font-bold text-gray-900">
              {t('stats.title') || 'Meus Ganhos'}
            </h1>
            <p className="text-gray-600 text-sm">
              {t('stats.subtitle') || 'Comissões e estatísticas'}
            </p>
          </div>

          <div className="flex-1 overflow-y-auto">
            {/* Main Earnings Card */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="p-4"
            >
              <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-2xl p-6 text-white mb-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-green-100 text-sm">
                      {t('stats.earnings.month') || 'Ganhos do Mês'}
                    </p>
                    <motion.p
                      initial={{ scale: 0.5 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 1 }}
                      className="text-3xl font-bold"
                    >
                      {formatCurrency(mockStats.this_month_earnings)}
                    </motion.p>
                  </div>
                  <div className="text-4xl">💰</div>
                </div>
                <div className="bg-white bg-opacity-20 rounded-lg p-3">
                  <div className="flex items-center justify-between text-sm">
                    <span>{t('stats.commission.rate') || 'Taxa de Comissão'}</span>
                    <span className="font-bold">15%</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Stats Grid */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1.2 }}
              className="px-4 mb-4"
            >
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-xl">🎫</span>
                    <span className="text-sm font-medium text-gray-600">
                      {t('stats.active.coupons') || 'Cupons Ativos'}
                    </span>
                  </div>
                  <motion.p
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 1.5 }}
                    className="text-2xl font-bold text-gray-900"
                  >
                    {mockStats.active_coupons}
                  </motion.p>
                </div>

                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-xl">✅</span>
                    <span className="text-sm font-medium text-gray-600">
                      {t('stats.total.redemptions') || 'Resgates Total'}
                    </span>
                  </div>
                  <motion.p
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 1.7 }}
                    className="text-2xl font-bold text-gray-900"
                  >
                    {mockStats.total_redemptions}
                  </motion.p>
                </div>

                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-xl">🏪</span>
                    <span className="text-sm font-medium text-gray-600">
                      {t('stats.partners') || 'Parceiros'}
                    </span>
                  </div>
                  <motion.p
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 1.9 }}
                    className="text-2xl font-bold text-gray-900"
                  >
                    {mockStats.vendors_recruited}
                  </motion.p>
                </div>

                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-xl">📈</span>
                    <span className="text-sm font-medium text-gray-600">
                      {t('stats.this.week') || 'Esta Semana'}
                    </span>
                  </div>
                  <motion.p
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 2.1 }}
                    className="text-xl font-bold text-green-600"
                  >
                    {formatCurrency(mockStats.this_week_earnings)}
                  </motion.p>
                </div>
              </div>
            </motion.div>

            {/* Recent Activity */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 2.3 }}
              className="px-4 pb-4"
            >
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="p-4 border-b">
                  <h3 className="font-bold text-gray-900">
                    {t('stats.recent.activity') || 'Atividade Recente'}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {t('stats.recent.desc') || 'Últimos resgates de cupons'}
                  </p>
                </div>
                <div className="divide-y">
                  {mockStats.recent_redemptions.map((redemption, index) => (
                    <motion.div
                      key={redemption.id}
                      initial={{ x: -50, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: 2.5 + index * 0.2 }}
                      className="p-4"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 mb-1">{redemption.coupon_title}</h4>
                          <p className="text-sm text-gray-600">{redemption.vendor_name}</p>
                          <p className="text-xs text-gray-500 mt-1">{formatDate(redemption.redeemed_at)}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-green-600">{formatCurrency(redemption.commission_earned)}</p>
                          <div className="flex items-center space-x-1 mt-1">
                            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                            <span className="text-xs text-green-600 font-medium">
                              {t('stats.paid') || 'Pago'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      )
    }
  ];

  useEffect(() => {
    if (isPlaying) {
      const timer = setTimeout(() => {
        if (currentStep < demoSteps.length - 1) {
          setCurrentStep(currentStep + 1);
        } else {
          setIsPlaying(false);
          setCurrentStep(0);
        }
      }, demoSteps[currentStep].duration);

      return () => clearTimeout(timer);
    }
  }, [currentStep, isPlaying, demoSteps]);

  const startDemo = () => {
    setCurrentStep(0);
    setIsPlaying(true);
  };

  const nextStep = () => {
    if (currentStep < demoSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className="bg-gray-100 flex flex-col demo-page mobile-full-height">
      {/* Demo Header - Fixed at top */}
      <div className="bg-white shadow-sm border-b p-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-gray-900">
              🎬 Demo QRSP
            </h1>
            <p className="text-xs text-gray-600">
              Passo {currentStep + 1} de {demoSteps.length}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {!isPlaying ? (
              <button
                onClick={startDemo}
                className="bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
              >
                ▶️ Iniciar
              </button>
            ) : (
              <button
                onClick={() => setIsPlaying(false)}
                className="bg-red-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
              >
                ⏸️ Pausar
              </button>
            )}
          </div>
        </div>
        
        {/* Progress Bar - Compact */}
        <div className="mt-2">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span className="truncate">{demoSteps[currentStep].title}</span>
            <span>{Math.round(((currentStep + 1) / demoSteps.length) * 100)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <motion.div
              className="bg-green-600 h-1.5 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${((currentStep + 1) / demoSteps.length) * 100}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </div>
      </div>

      {/* Demo Content - Scrollable middle section */}
      <div className="flex-1 relative overflow-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -50, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="min-h-full"
          >
            {demoSteps[currentStep].component}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Demo Controls - Fixed at bottom */}
      <div className="bg-white border-t p-2 flex-shrink-0" style={{ paddingBottom: 'max(8px, env(safe-area-inset-bottom))' }}>
        {/* Step info row */}
        <div className="text-center mb-2">
          <h3 className="font-semibold text-gray-900 text-sm truncate">{demoSteps[currentStep].title}</h3>
          <p className="text-xs text-gray-500 truncate">{demoSteps[currentStep].description}</p>
        </div>
        
        {/* Navigation buttons row */}
        <div className="flex items-center justify-between gap-2">
          <button
            onClick={prevStep}
            disabled={currentStep === 0}
            className="flex items-center justify-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 transition-colors text-sm min-w-0 flex-1"
          >
            <span className="truncate">← Anterior</span>
          </button>
          
          <div className="text-xs text-gray-500 px-2 whitespace-nowrap">
            {currentStep + 1}/{demoSteps.length}
          </div>
          
          <button
            onClick={nextStep}
            disabled={currentStep === demoSteps.length - 1}
            className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700 transition-colors text-sm min-w-0 flex-1"
          >
            <span className="truncate">Próximo →</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export const MobileDemoFlow: React.FC = () => {
  return (
    <LanguageProvider>
      <DemoFlowContent />
    </LanguageProvider>
  );
};